import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import css from "./LocationPicker.scss"
import { getAssetPath, setCookie } from "@commonUtils"
import ImageLoader from "@components/ImageLoader/ImageLoader"
import GaEvents from "../Header/GaEvents"
import {
    fetchCities,
    updateCurrentCity,
    getCurrentLocationCity,
    getCurrentCityDetails,
    showLoading,
} from "./reducer"
import { sendMixpanelTrackEvent } from "@lib/gtmUtility"

const gpsIconPath = getAssetPath("svgs/icons/gps_24px_primary.svg")
const locationIcon = getAssetPath("svgs/icons/location_18px_grey.svg")

const CitiesDropdown = ({ cities, topCities, citiesLabel, topCitiesLabel, onCityClick, showHeaders }) => {
    const ItemSection = ({ header, cities }) => (
        <>
            {showHeaders && <div className={`smallSemiBold textPrimary ${css.cityHeader}`}>{header}</div>}
            {cities?.length
                ? cities.map((city) => (
                      <div
                          key={city.name}
                          className={`bodyRegular ${css.cityItem}`}
                          onClick={() => onCityClick(city.name)}
                      >
                          {city.name}
                      </div>
                  ))
                : null}
        </>
    )

    return (
        <div className={`${css.citiesDropdown}`}>
            {showHeaders && <ItemSection header={topCitiesLabel} cities={topCities} />}
            <ItemSection header={citiesLabel} cities={cities} />
        </div>
    )
}

const ListOfCities = ({
    placeholder,
    getCurrentLocation,
    value,
    onChange,
    topCities,
    allCities,
    citiesLabel,
    topCitiesLabel,
    onCitySelect,
    showHeaders,
    pageName,
}) => {
    return (
        <>
            <ImageLoader height="24px" width="24px" src={locationIcon} alt="Location Icon" preload={true} />
            <label htmlFor="location-selector" className="visuallyHidden">
                {placeholder}
            </label>
            <input
                placeholder={placeholder}
                title={placeholder}
                aria-label={placeholder}
                value={value}
                name="city"
                className={`bodySemiBold ${css.cityInput}`}
                onChange={(e) => onChange(e.target.value)}
                id="location-selector"
                autoComplete="off"
                onClick={() => {
                    GaEvents.onLocationChangeClick()
                    sendMixpanelTrackEvent("location_clicked", {
                        page_name: pageName,
                    })
                }}
            />
            <ImageLoader
                onClick={getCurrentLocation}
                height="24px"
                width="24px"
                src={gpsIconPath}
                alt="GPS icon"
                preload={true}
            />
            <CitiesDropdown
                topCities={topCities}
                cities={allCities}
                citiesLabel={citiesLabel}
                topCitiesLabel={topCitiesLabel}
                onCityClick={onCitySelect}
                showHeaders={showHeaders}
            />
        </>
    )
}

const LocationPicker = () => {
    const dispatch = useDispatch()
    const { cities, topCities, currentCity, isFetching } = useSelector((state) => state.locationsReducer)
    const { pageName } = useSelector((state) => state.shellReducer)

    const [search, setSearch] = useState(currentCity)
    const [showGPS, toggleGPSControl] = useState(false)
    const [firstRender, setFirstRender] = useState(true)
    const [allCities, setAllCities] = useState(cities.data)

    useEffect(() => {
        if (!cities.data.length) {
            dispatch(fetchCities())
        }

        if ("geolocation" in navigator && !showGPS) {
            toggleGPSControl(true)
        }
    }, [])

    useEffect(() => {
        setSearch(currentCity)
    }, [currentCity])

    useEffect(() => {
        setAllCities(cities.data)
    }, [cities.data.length])

    const filterCities = (value) => {
        setSearch(value)
        setFirstRender(false)
        if (value.length === 0) {
            setAllCities(cities.data)
        } else {
            const filteredCities = cities.data.filter((city) => {
                return city.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
            })
            setAllCities(filteredCities)
        }
    }

    const onCitySelect = (city) => {
        if (currentCity !== city) {
            GaEvents.onSelectingCity(city)
            setSearch(city)
            setCookie("city", city)
            dispatch(updateCurrentCity(city))
            dispatch(getCurrentCityDetails(city))
        }
    }

    const getCurrentLocation = () => {
        if (showGPS) {
            dispatch(showLoading())
            // geolocation is available
            GaEvents.onCityAutoDetectClick()
            navigator.geolocation.getCurrentPosition(
                // get location of user
                function (position) {
                    dispatch(
                        getCurrentLocationCity({
                            lat: position.coords.latitude,
                            long: position.coords.longitude,
                        })
                    )
                },
                function () {
                    // if user denied permission
                }
            )
        }
    }

    return (
        <div className={`flex alignCenter ${css.container}`}>
            <ListOfCities
                placeholder="Enter your city"
                getCurrentLocation={getCurrentLocation}
                value={isFetching ? "Loading" : search}
                onChange={filterCities}
                topCities={topCities.data}
                allCities={allCities}
                citiesLabel={cities.label}
                topCitiesLabel={topCities.label}
                onCitySelect={onCitySelect}
                showHeaders={firstRender || !search.length}
                pageName={pageName}
            />
        </div>
    )
}

CitiesDropdown.propTypes = {
    cities: PropTypes.array,
    topCities: PropTypes.array,
    citiesLabel: PropTypes.string,
    topCitiesLabel: PropTypes.string,
    onCityClick: PropTypes.func,
    showHeaders: PropTypes.bool,
}

ListOfCities.propTypes = {
    placeholder: PropTypes.string,
    getCurrentLocation: PropTypes.func,
    value: PropTypes.string,
    onChange: PropTypes.func,
    topCities: PropTypes.array,
    allCities: PropTypes.array,
    citiesLabel: PropTypes.string,
    topCitiesLabel: PropTypes.string,
    onCitySelect: PropTypes.func,
    showHeaders: PropTypes.bool,
}

export default LocationPicker
